import type { CsvRow } from "../types/draft";
import { parsePickedAt } from "./playerHelpers";
import { NFL_DRAFT_CUTOFF } from "../config/season";

// Underdog groups tournaments by whether they were drafted before or after the
// NFL draft. We classify by the pick timestamp (Picked At), which is the actual
// definition and works regardless of roster size / format.

export type TournamentGroupKey = "pre" | "regular" | "undated";

export const TOURNAMENT_GROUP_LABELS: Record<TournamentGroupKey, string> = {
  pre: "Pre-Draft",
  regular: "Regular Season",
  undated: "Undated",
};

/** Classify a single draft by its pick timestamp. null when the date is missing. */
export function classifyDraftDate(pickedAt: string | undefined | null): "pre" | "regular" | null {
  const d = parsePickedAt(pickedAt ?? undefined);
  if (!d) return null;
  return d.toISOString().slice(0, 10) < NFL_DRAFT_CUTOFF ? "pre" : "regular";
}

export interface TournamentGroups {
  pre: string[];
  regular: string[];
  undated: string[];
  /** Tournament title → group key. */
  byTitle: Map<string, TournamentGroupKey>;
}

/**
 * Group tournament titles into pre-draft / regular-season / undated using the
 * draft dates seen for each title. A title is classified by the majority of its
 * rows' dates (drafts within one tournament are all on the same side in practice).
 */
export function groupTournamentsByType(rawData: CsvRow[]): TournamentGroups {
  const tally = new Map<string, { pre: number; regular: number; undated: number }>();
  for (const row of rawData) {
    const title = (row["Tournament Title"] ?? "").trim();
    if (!title) continue;
    const t = tally.get(title) ?? { pre: 0, regular: 0, undated: 0 };
    const cls = classifyDraftDate(row["Picked At"]);
    if (cls === "pre") t.pre++;
    else if (cls === "regular") t.regular++;
    else t.undated++;
    tally.set(title, t);
  }

  const pre: string[] = [];
  const regular: string[] = [];
  const undated: string[] = [];
  const byTitle = new Map<string, TournamentGroupKey>();

  for (const [title, t] of tally) {
    let group: TournamentGroupKey;
    if (t.pre === 0 && t.regular === 0) group = "undated";
    else group = t.pre >= t.regular ? "pre" : "regular";
    byTitle.set(title, group);
    (group === "pre" ? pre : group === "regular" ? regular : undated).push(title);
  }

  const byName = (a: string, b: string) => a.localeCompare(b);
  pre.sort(byName);
  regular.sort(byName);
  undated.sort(byName);
  return { pre, regular, undated, byTitle };
}
